<template>
  <Card class="rounded-xl border border-outline-variant bg-surface-container-lowest p-8 shadow-md">
    <div class="mb-8 flex items-start justify-between gap-4">
      <div>
        <p class="font-label text-label-md uppercase text-secondary">
          Status
        </p>
        <h2 class="mt-2 font-headline text-display-sm text-primary">
          {{ t('status.title') }}
        </h2>
      </div>
      <div
        class="rounded-full px-3 py-1 font-label text-label-sm transition-colors duration-300"
        :class="
          isExploring
            ? 'bg-primary-container text-white'
            : 'bg-surface-container-high text-on-surface-variant'
        "
      >
        {{ isExploring ? 'Active' : 'Resting' }}
      </div>
    </div>

    <div v-if="isExploring" class="space-y-4">
      <div class="rounded-xl bg-surface-container-low p-5 text-center">
        <div class="mb-3">
          <div class="font-headline text-display-md text-primary">
            {{ timer.display }}
          </div>
          <div class="mt-1 font-label text-label-md uppercase text-on-surface-variant">
            {{ t('status.remaining') }}
          </div>
        </div>
        <div class="h-2 overflow-hidden rounded-full bg-surface-container-high">
          <div
            class="h-full rounded-full bg-secondary transition-all duration-1000 ease-linear"
            :style="{ width: progressPercentage + '%' }"
          />
        </div>
      </div>

      <div class="flex justify-center">
        <Button :loading="loading" variant="danger" size="sm" form @click="endStatus">
          {{ loading ? t('status.ending') : t('status.endSession') }}
        </Button>
      </div>
    </div>

    <div v-else class="space-y-4">
      <Button
        :loading="loading"
        variant="primary"
        size="lg"
        form
        class="w-full justify-center py-4 text-label-lg"
        @click="setOutToExplore"
      >
        {{ loading ? t('status.activating') : t('status.outToExplore') }}
      </Button>
      <div class="rounded-xl bg-surface-container-low p-4">
        <p class="text-center font-body text-body-sm text-on-surface-variant">
          {{ t('status.exploreDescription') }}
        </p>
      </div>
    </div>
  </Card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '../stores/user'
import { useTimer } from '../composables/useTimer'
import Card from './ui/Card.vue'
import Button from './ui/Button.vue'

const { t } = useI18n()

const userStore = useUserStore()
const loading = ref(false)

// Reactive end time from profile
const endTime = computed(() => userStore.profile?.zin_in_spel_until)
const isActiveInProfile = computed(() => userStore.profile?.zin_in_spel)
const lastActive = computed(() => userStore.profile?.last_active)

// Timer composable
const { timeRemaining: timer, isExploring: timerActive, startTimer, stopTimer } = useTimer(endTime)

// Combined active status (both database flag and timer)
const isExploring = computed(() => {
  return isActiveInProfile.value && timerActive.value
})

// Progress percentage for progress bar
const progressPercentage = computed(() => {
  if (!timer.value || timer.value.expired) return 0

  const totalDuration = 90 * 60 // 90 minutes in seconds
  const remaining = timer.value.totalSeconds
  return Math.max(0, (remaining / totalDuration) * 100)
})

// Watch for timer expiration
watch(timer, newTimer => {
  if (newTimer && newTimer.expired && isActiveInProfile.value) {
    // Update profile to mark as inactive
    updateProfileStatus(false)
  }
})

async function setOutToExplore() {
  loading.value = true
  try {
    await userStore.toggleZinInSpel()
    startTimer()
  } catch (error) {
    console.error('Failed to set out to explore:', error)
    alert('Failed to set out to explore. Please try again.')
  } finally {
    loading.value = false
  }
}

async function endStatus() {
  loading.value = true
  try {
    await userStore.endZinInSpel()
    stopTimer()
  } catch (error) {
    console.error('Failed to end status:', error)
    alert('Failed to end status. Please try again.')
  } finally {
    loading.value = false
  }
}

async function updateProfileStatus(active) {
  try {
    await userStore.updateProfile({
      zin_in_spel: active,
      zin_in_spel_until: active ? userStore.profile.zin_in_spel_until : null
    })
  } catch (error) {
    console.error('Failed to update profile status:', error)
  }
}

function formatLastActive(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `You've been out exploring since ${diffMins} minutes ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hours ago`

  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} days ago`
}
</script>
